import {useEffect, useState} from 'react';
import {Loader2, Trash2} from 'lucide-react';
import {Button} from '@/components/ui/button';
import {Input} from '@/components/ui/input';
import {Label} from '@/components/ui/label';
import {Textarea} from '@/components/ui/textarea';
import {toast} from 'sonner';
import {useUpdateCourseItem} from '@/hooks/hooks';
import ConfirmationModal from './confirmation-modal';

/** Mirrors POLICY_LIMITS on the server, which clamps anything out of range. */
const LIMITS = {
  maxReviewsPerReflection: {min: 1, max: 25, fallback: 10},
  requiredReviewsToUnlock: {min: 0, max: 25, fallback: 10},
  minReviewsToReveal: {min: 1, max: 25, fallback: 3},
} as const;

type PolicyField = keyof typeof LIMITS;

/**
 * Stands in when the item carries no description of its own.
 *
 * The shared UpdateItemBody validator requires a non-empty description for
 * every item type, and this panel does not edit that field — so an item created
 * without one could never be saved. Defaulting here keeps the constraint intact
 * for the item types that rely on it.
 */
const DEFAULT_DESCRIPTION =
  'Write what you learned, then review your peers anonymously';

interface ReflectionItemEditorProps {
  itemId: string;
  courseId: string;
  versionId: string;
  name: string;
  description?: string;
  details?: {
    prompt?: string;
    maxReviewsPerReflection?: number;
    requiredReviewsToUnlock?: number;
    minReviewsToReveal?: number;
  };
  onSaved: () => void;
  onDelete?: () => void;
}

function NumberField({
  id,
  label,
  question,
  example,
  field,
  value,
  onChange,
}: {
  id: string;
  label: string;
  /** The plain-language question this field answers. */
  question: string;
  /** A worked example using the current (or default) value. */
  example: string;
  field: PolicyField;
  value: string;
  onChange: (next: string) => void;
}) {
  const {min, max, fallback} = LIMITS[field];
  return (
    <div className="space-y-1.5 rounded-lg border p-3">
      <Label htmlFor={id} className="text-sm font-semibold">
        {label}
      </Label>
      <p className="text-xs text-muted-foreground">{question}</p>
      <Input
        id={id}
        type="number"
        inputMode="numeric"
        min={min}
        max={max}
        value={value}
        placeholder={`${fallback} (default)`}
        onChange={e => onChange(e.target.value)}
      />
      <p className="text-xs text-muted-foreground">
        <span className="font-medium text-foreground">e.g. </span>
        {example}
      </p>
    </div>
  );
}

/**
 * Instructor settings for one reflection item.
 *
 * Every field is optional: left blank, the item inherits the platform default,
 * which is why empty is stored as undefined rather than coerced to a number.
 */
export default function ReflectionItemEditor({
  itemId,
  courseId,
  versionId,
  name,
  description,
  details,
  onSaved,
  onDelete,
}: ReflectionItemEditorProps) {
  const {mutateAsync: updateItem, isPending} = useUpdateCourseItem();
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const asText = (n?: number) => (typeof n === 'number' ? String(n) : '');

  const [prompt, setPrompt] = useState(details?.prompt ?? '');
  const [maxReviews, setMaxReviews] = useState(
    asText(details?.maxReviewsPerReflection),
  );
  const [requiredReviews, setRequiredReviews] = useState(
    asText(details?.requiredReviewsToUnlock),
  );
  const [minReveal, setMinReveal] = useState(
    asText(details?.minReviewsToReveal),
  );

  // Selecting a different reflection item reuses this component instance.
  useEffect(() => {
    setPrompt(details?.prompt ?? '');
    setMaxReviews(asText(details?.maxReviewsPerReflection));
    setRequiredReviews(asText(details?.requiredReviewsToUnlock));
    setMinReveal(asText(details?.minReviewsToReveal));
  }, [itemId]); // eslint-disable-line react-hooks/exhaustive-deps

  const parse = (raw: string, field: PolicyField): number | undefined => {
    if (raw.trim() === '') return undefined;
    const n = Number(raw);
    if (!Number.isFinite(n)) return undefined;
    const {min, max} = LIMITS[field];
    return Math.min(Math.max(Math.round(n), min), max);
  };

  const effectiveMax =
    parse(maxReviews, 'maxReviewsPerReflection') ??
    LIMITS.maxReviewsPerReflection.fallback;
  const effectiveReveal =
    parse(minReveal, 'minReviewsToReveal') ?? LIMITS.minReviewsToReveal.fallback;
  const revealExceedsCap = effectiveReveal > effectiveMax;

  const handleSave = async () => {
    try {
      await updateItem({
        params: {path: {courseId, versionId, itemId}},
        body: {
          name: name?.trim() || 'Reflection',
          description: description?.trim() || DEFAULT_DESCRIPTION,
          type: 'REFLECTION',
          // The update endpoint carries the payload on `details`, unlike create
          // which uses a per-type field (`reflectionDetails`).
          details: {
            prompt: prompt.trim() === '' ? undefined : prompt.trim(),
            maxReviewsPerReflection: parse(
              maxReviews,
              'maxReviewsPerReflection',
            ),
            requiredReviewsToUnlock: parse(
              requiredReviews,
              'requiredReviewsToUnlock',
            ),
            minReviewsToReveal: parse(minReveal, 'minReviewsToReveal'),
          },
        },
      } as any);
      toast.success('Reflection settings saved');
      onSaved();
    } catch (error: unknown) {
      // Surface what the server actually objected to; a bare "could not save"
      // leaves no way to tell a validation error from a permission one.
      const err = error as {
        response?: {data?: {message?: string}};
        data?: {message?: string};
        message?: string;
      };
      const detail =
        err?.response?.data?.message ?? err?.data?.message ?? err?.message;
      console.error('Reflection settings save failed', error);
      toast.error(
        detail
          ? `Could not save: ${detail}`
          : 'Could not save the reflection settings',
      );
    }
  };

  return (
    <div className="space-y-5">
      <div className="space-y-1.5">
        <Label htmlFor="reflection-prompt">Prompt</Label>
        <Textarea
          id="reflection-prompt"
          rows={3}
          value={prompt}
          placeholder="What did you take away from this section?"
          onChange={e => setPrompt(e.target.value)}
        />
        <p className="text-xs text-muted-foreground">
          Shown above the editor. Left blank, students see the default question.
        </p>
      </div>

      <div className="space-y-2 rounded-lg bg-muted/40 p-4">
        <p className="text-sm font-semibold">Peer review rules</p>
        <p className="text-xs text-muted-foreground">
          Every student writes one reflection, then reviews others' anonymously.
          These three numbers decide how many reviews happen and when a student
          finally sees their score. Leave any blank to use the default.
        </p>
      </div>

      <div className="grid gap-4 sm:grid-cols-3">
        <NumberField
          id="max-reviews"
          label="1. Reviewers per answer"
          question="How many classmates score each reflection?"
          example={`each reflection is scored by up to ${
            parse(maxReviews, 'maxReviewsPerReflection') ??
            LIMITS.maxReviewsPerReflection.fallback
          } classmates, then stops taking reviews.`}
          field="maxReviewsPerReflection"
          value={maxReviews}
          onChange={setMaxReviews}
        />
        <NumberField
          id="required-reviews"
          label="2. Reviews to give"
          question="How many others must a student review to see their own score?"
          example={
            (parse(requiredReviews, 'requiredReviewsToUnlock') ??
              LIMITS.requiredReviewsToUnlock.fallback) === 0
              ? 'set to 0, so a student sees their score without reviewing anyone.'
              : `a student must review ${
                  parse(requiredReviews, 'requiredReviewsToUnlock') ??
                  LIMITS.requiredReviewsToUnlock.fallback
                } classmates before their own score is shown.`
          }
          field="requiredReviewsToUnlock"
          value={requiredReviews}
          onChange={setRequiredReviews}
        />
        <NumberField
          id="min-reveal"
          label="3. Reviews to receive"
          question="How many scores must a reflection collect before its average shows?"
          example={`the average appears only once ${
            parse(minReveal, 'minReviewsToReveal') ??
            LIMITS.minReviewsToReveal.fallback
          } classmates have scored it — fewer would be too random.`}
          field="minReviewsToReveal"
          value={minReveal}
          onChange={setMinReveal}
        />
      </div>

      {revealExceedsCap ? (
        <p className="rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-xs text-amber-700 dark:text-amber-400">
          A score needs {effectiveReveal} reviews but only {effectiveMax} can be
          given, so it would never appear. The server will lower the threshold to{' '}
          {effectiveMax}.
        </p>
      ) : null}

      <div className="flex items-center gap-2">
        <Button onClick={handleSave} disabled={isPending}>
          {isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
          Save settings
        </Button>
        {onDelete ? (
          <Button
            variant="outline"
            className="border-border bg-background"
            onClick={() => setShowDeleteModal(true)}
          >
            <Trash2 className="mr-2 h-4 w-4" />
            Delete
          </Button>
        ) : null}
        <p className="text-xs text-muted-foreground">
          Blank fields use the platform defaults ({
            LIMITS.maxReviewsPerReflection.fallback
          }
          /{LIMITS.requiredReviewsToUnlock.fallback}/
          {LIMITS.minReviewsToReveal.fallback}).
        </p>
      </div>
      {onDelete ? (
        <ConfirmationModal
          isOpen={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          onConfirm={onDelete}
          title="Delete Reflection"
          description="This will delete this reflection item, including any student submissions and reviews. Are you sure?"
          confirmText="Delete"
          cancelText="Cancel"
          isDestructive={true}
        />
      ) : null}
    </div>
  );
}
